import { useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { SearchSelect } from "../waba/MetricsTab";
import {
  PaperclipIcon, Pill, SourcePill, SubjectPills, fmtDateTime, fmtDue, inputStyle, overdueLabel, useTaskOptions,
  type TaskCard,
} from "./common";

type Status = "all" | "open" | "overdue" | "done";
type Board = { today: TaskCard[]; tomorrow: TaskCard[]; upcoming: TaskCard[] };

const STATUS: [Status, string][] = [["all", "Todos"], ["open", "Em aberto"], ["overdue", "Atrasados"], ["done", "Finalizados"]];

export function TaskBoard({ onOpen }: { onOpen: (id: string) => void }) {
  const [managerId, setManagerId] = useState("");
  const [agentId, setAgentId] = useState("");
  const [subject, setSubject] = useState("");
  const [dateField, setDateField] = useState<"due" | "created" | "finished">("due");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [status, setStatus] = useState<Status>("all");

  const { data: opts } = useTaskOptions();
  const { data: subjects = [] } = useQuery({ queryKey: ["tasks", "subjects"], queryFn: () => api.get<string[]>("/tasks/subjects") });
  const managers = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of opts?.agents ?? []) m.set(a.manager_id, a.manager_name ?? "—");
    return [...m].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [opts]);

  const qs = new URLSearchParams();
  if (managerId) qs.set("manager_id", managerId);
  if (agentId) qs.set("agent_id", agentId);
  if (subject) qs.set("subject", subject);
  if (from || to) qs.set("date_field", dateField);
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  const params = qs.toString();

  const board = useQuery({
    queryKey: ["tasks", "board", params],
    queryFn: () => api.get<Board>(`/tasks/board?${params}`),
    refetchInterval: 60_000,
  });
  const showFinished = status === "all" || status === "done";
  const finished = useInfiniteQuery({
    queryKey: ["tasks", "finished", params],
    queryFn: ({ pageParam }) =>
      api.get<{ items: TaskCard[]; next_cursor: string | null }>(`/tasks/finished?${params}${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ""}`),
    initialPageParam: "",
    getNextPageParam: last => last.next_cursor ?? undefined,
    enabled: showFinished,
  });

  const b = board.data ?? { today: [], tomorrow: [], upcoming: [] };
  const keep = (c: TaskCard) =>
    status === "all" || (status === "open" && c.status === "open") || (status === "overdue" && c.overdue) || (status === "done" && c.status === "done");
  const columns: { key: string; title: string; cards: TaskCard[]; footer?: React.ReactNode }[] = [
    { key: "today", title: "Hoje", cards: b.today.filter(keep) },
    ...(status === "all" || status === "open" ? [
      { key: "tomorrow", title: "Amanhã", cards: b.tomorrow },
      { key: "upcoming", title: "Próximas", cards: b.upcoming },
    ] : []),
    ...(showFinished ? [{
      key: "finished", title: "Finalizados", cards: finished.data?.pages.flatMap(p => p.items) ?? [],
      footer: finished.hasNextPage ? (
        <button onClick={() => finished.fetchNextPage()} disabled={finished.isFetchingNextPage} style={{ width: "100%", padding: "8px", fontSize: 12, background: "none", border: "1px dashed var(--line-strong)", borderRadius: 8, color: "var(--ink-secondary)", cursor: "pointer" }}>
          {finished.isFetchingNextPage ? "Carregando…" : "Carregar mais"}
        </button>
      ) : null,
    }] : []),
  ];
  const hasDate = !!(from || to);

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Filtros */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <SearchSelect value={managerId} onChange={setManagerId} options={managers} placeholder="Todos os gestores" />
        <SearchSelect value={agentId} onChange={setAgentId} options={(opts?.agents ?? []).map(a => ({ value: a.id, label: a.name }))} placeholder="Todos os agentes" />
        <SearchSelect value={subject} onChange={setSubject} options={subjects.map(s => ({ value: s, label: s }))} placeholder="Todos os assuntos" />
        <select value={dateField} onChange={e => setDateField(e.target.value as typeof dateField)} aria-label="Tipo de data" style={{ ...inputStyle, width: "auto", padding: "6px 8px", fontSize: 12 }}>
          <option value="due">Prazo</option>
          <option value="created">Criação</option>
          <option value="finished">Finalização</option>
        </select>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} aria-label="De" style={{ ...inputStyle, width: 140, padding: "6px 8px", fontSize: 12 }} />
        <input type="date" value={to} onChange={e => setTo(e.target.value)} aria-label="Até" style={{ ...inputStyle, width: 140, padding: "6px 8px", fontSize: 12 }} />
        {hasDate && <button onClick={() => { setFrom(""); setTo(""); }} style={{ background: "none", border: "none", color: "var(--ink-muted)", fontSize: 12, cursor: "pointer" }}>Limpar datas</button>}
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", gap: 6 }}>
          {STATUS.map(([id, label]) => (
            <button key={id} onClick={() => setStatus(id)} style={{ padding: "5px 12px", borderRadius: 99, fontSize: 12, cursor: "pointer", background: status === id ? "var(--brand-soft)" : "var(--surface-hover)", border: `1px solid ${status === id ? "var(--brand)" : "var(--line)"}`, color: status === id ? "var(--brand)" : "var(--ink-secondary)" }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {board.isError && <span style={{ fontSize: 12, color: "var(--danger)" }}>Não foi possível carregar o quadro.</span>}

      {/* Colunas */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns.length}, minmax(260px, 1fr))`, gap: 14, alignItems: "start", overflowX: "auto" }}>
        {columns.map(col => (
          <div key={col.key} style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 10, display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 4px 4px" }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{col.title}</span>
              <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>{col.cards.length}{col.key === "finished" && finished.hasNextPage ? "+" : ""}</span>
            </div>
            {col.cards.map(c => <Card key={c.id} c={c} onOpen={onOpen} />)}
            {!col.cards.length && !board.isLoading && (
              <div style={{ fontSize: 12, color: "var(--ink-dim)", padding: "14px 4px", textAlign: "center" }}>Nada aqui.</div>
            )}
            {col.footer}
          </div>
        ))}
      </div>
    </div>
  );
}

function Card({ c, onOpen }: { c: TaskCard; onOpen: (id: string) => void }) {
  const tone = c.status === "done"
    ? { bg: "var(--ok-soft)", line: "var(--ok-line)", due: "var(--ok)" }
    : c.overdue
      ? { bg: "var(--danger-soft)", line: "var(--danger-line)", due: "var(--danger)" }
      : { bg: "var(--surface-card)", line: "var(--line)", due: "var(--ink-secondary)" };
  return (
    <button
      onClick={() => onOpen(c.id)}
      style={{ textAlign: "left", width: "100%", background: tone.bg, border: `1px solid ${tone.line}`, borderRadius: 10, padding: 12, cursor: "pointer", display: "flex", flexDirection: "column", gap: 8, fontFamily: "inherit" }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
        {c.overdue && <Pill color="var(--danger)" bg="var(--surface-card)" line="var(--danger-line)">{overdueLabel(c.due_date, c.due_time)}</Pill>}
        <SubjectPills subjects={c.subjects} />
        <SourcePill t={c} />
      </div>
      <div style={{ fontSize: 13.5, fontWeight: 500, color: "var(--ink)", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
        {c.title}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: "var(--ink-muted)", flexWrap: "wrap" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 140 }} title={`${c.agent_name} · ${c.manager_name ?? "—"}`}>{c.agent_name}</span>
        <span style={{ color: tone.due, fontWeight: c.overdue ? 600 : 400 }}>
          {c.status === "done" && c.finished_at ? `✓ ${fmtDateTime(c.finished_at)}` : fmtDue(c.due_date, c.due_time)}
        </span>
        {c.postpones > 0 && <Pill color="var(--warn)" bg="var(--warn-soft)" line="var(--warn-line)" title="Vezes que o prazo mudou">adiada {c.postpones}x</Pill>}
        {c.attachments > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }} title="Anexos"><PaperclipIcon />{c.attachments}</span>}
      </div>
    </button>
  );
}
